import { useCallback, useEffect, useRef, useState } from "react";

/**
 * The always-visible proof that a real program sits behind this page.
 *
 * Hidden proofs (the raw-server panel, /docs) require the visitor to go looking. This
 * strip is the first-glance version: a round-trip time the visitor's own browser just
 * measured, a ledger count that comes from a real COUNT over the decision table, and a
 * "last decision" clock that ticks. None of these can hold still on a static mock - and
 * when the visitor sends a transfer, the count visibly goes up by one in front of them.
 *
 * Honesty over polish: if the server does not answer, the strip goes red and says so.
 * A fake strip would never show its own failure.
 */

interface DemoStats {
  decisions_total: number;
  last_decided_at: string | null;
  predictor_backend: string;
  server_time: string;
}

/** Refresh cadence. The demo surface is rate-limited per IP, so the strip polls gently
 *  and relies on the `safefds:decision` event for instant updates after visitor actions. */
const POLL_MS = 25_000;

function agoText(seconds: number | null): string {
  if (seconds === null) return "아직 없음";
  if (seconds < 60) return `${seconds}초 전`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}분 전`;
  return `${Math.floor(seconds / 3600)}시간 전`;
}

interface Props {
  /** Hands the visitor over to the playground's first mission (the 10-second verify). */
  onVerify?: () => void;
}

export function LiveProofStrip({ onVerify }: Props) {
  const [stats, setStats] = useState<DemoStats | null>(null);
  const [roundTripMs, setRoundTripMs] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);
  const [delta, setDelta] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const prevTotal = useRef<number | null>(null);
  // Client clocks disagree with the server clock; the skew is measured at each refresh
  // so "N초 전" never goes negative on a fast visitor clock.
  const clockSkewMs = useRef(0);

  const refresh = useCallback(async () => {
    const t0 = performance.now();
    try {
      const response = await fetch("/v1/demo/stats");
      if (!response.ok) throw new Error(String(response.status));
      const body = (await response.json()) as DemoStats;
      setRoundTripMs(Math.max(1, Math.round(performance.now() - t0)));
      clockSkewMs.current = Date.now() - new Date(body.server_time).getTime();
      if (prevTotal.current !== null && body.decisions_total > prevTotal.current) {
        setDelta(body.decisions_total - prevTotal.current);
        setTimeout(() => setDelta(null), 5000);
      }
      prevTotal.current = body.decisions_total;
      setStats(body);
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const poll = setInterval(() => void refresh(), POLL_MS);
    const tick = setInterval(() => setNow(Date.now()), 1000);
    const onDecision = () => void refresh();
    window.addEventListener("safefds:decision", onDecision);
    return () => {
      clearInterval(poll);
      clearInterval(tick);
      window.removeEventListener("safefds:decision", onDecision);
    };
  }, [refresh]);

  if (failed) {
    return (
      <div className="live-proof live-proof--down">
        🔴 지금 서버가 응답하지 않아요. 이 줄은 실제 서버 확인 결과만 보여주기 때문에,
        확인이 실패하면 이렇게 실패로 표시합니다. 잠시 후 자동으로 다시 시도해요.
      </div>
    );
  }
  if (!stats) {
    return <div className="live-proof">⏳ 서버 연결 확인 중…</div>;
  }

  const lastAgoSeconds = stats.last_decided_at
    ? Math.max(
        0,
        Math.round(
          (now - clockSkewMs.current - new Date(stats.last_decided_at).getTime()) / 1000,
        ),
      )
    : null;

  return (
    <details className="live-proof">
      <summary>
        <span className="live-dot" aria-hidden="true" />
        <strong>
          {stats.predictor_backend === "ml"
            ? "SF ML 엔진(위험도를 계산하는 AI) 가동 중"
            : "SafeFDS 가동 중 (규칙 엔진)"}
        </strong>{" "}
        · 왕복 {roundTripMs}ms · 판정 장부{" "}
        <strong>{stats.decisions_total.toLocaleString()}건</strong>
        {delta !== null && <strong className="live-delta"> +{delta} 방금!</strong>} · 마지막
        판정 {agoText(lastAgoSeconds)}
        {onVerify && (
          <button
            type="button"
            className="live-verify-btn"
            onClick={(event) => {
              // A button inside <summary> must not also toggle the details.
              event.preventDefault();
              event.stopPropagation();
              onVerify();
            }}
          >
            🔎 10초 검증하기
          </button>
        )}
      </summary>
      <div className="live-proof-detail">
        {stats.predictor_backend === "ml" && (
          <p>
            <strong>SF ML 엔진</strong>은 <strong>SafeFDS Machine Learning Engine</strong>의
            줄임말이에요 — 송금 한 건이 얼마나 위험한지 <strong>점수를 매기는 부분</strong>
            입니다. 학습시킨 AI 모델이 API 서버 안에서 직접 계산해요(별도 모델 서버 없음).
            자세한 이야기는 &quot;만든 이야기&quot; 탭 5막에 있습니다.
          </p>
        )}
        <p>
          <strong>왕복 시간</strong>은 지금 이 브라우저가 서버에 다녀온 실측값이고,{" "}
          <strong>판정 장부</strong>는 서버 데이터베이스의 실제 판정 개수예요.
          &quot;직접 해보기&quot;에서 송금을 보내면 이 숫자가 눈앞에서 1 올라가고, 방금
          발급된 고유 판정번호로 <strong>서버의 저장 기록을 별도 요청으로 다시
          조회</strong>해서 보여드립니다. 지금 판정을 내리는 엔진:{" "}
          {stats.predictor_backend === "ml"
            ? "🤖 SF ML 엔진 — 학습된 ML 모델을 API 프로세스 안에서 직접 추론 (서버가 보고한 값)"
            : "📏 규칙 스코어러 (서버가 보고한 값)"}
        </p>
        <p className="muted">직접 확인하고 싶다면 위의 🔎 10초 검증하기를 눌러보세요.</p>
      </div>
    </details>
  );
}
